import cn from "classnames";
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "web-ui/components/internal/Badge";
import Tag from "web-ui/components/internal/Tag";
import { buttonKeyDownHandler } from "web-ui/util/a11y";

import type { VoiceListVoice } from "../../api/generalTts";

import styles from "./VoiceCard.module.scss";
import {
  formatCharacter,
  formatLanguage,
  formatProvider,
  titleCaseFormatter,
} from "./VoiceFinder";

interface Props {
  onSelect?: () => void;
  state?: "enabled" | "selected";
  tabIndex?: number;
  unavailable?: boolean;
  voice: VoiceListVoice;
}

const VoiceCard = ({
  onSelect,
  state = "enabled",
  tabIndex = 0,
  voice,
}: Props) => {
  const [searchParams] = useSearchParams();

  // TODO: add quality information
  const quality = undefined as "high" | "acceptable" | "low" | undefined;

  return (
    <div
      aria-selected={state === "selected"}
      className={cn(styles.card, {
        [styles.selected]: state === "selected",
        [styles.unavailable]: voice.status === "UNAVAILABLE",
      })}
      onClick={() => onSelect?.()}
      onKeyDown={buttonKeyDownHandler(() => onSelect?.())}
      tabIndex={tabIndex}
    >
      <div className={styles.header}>
        <span className={styles.name}>{voice.alias}</span>
        {quality === "high" ? (
          <Badge className={styles.badge} color="green" size="s">
            High quality
          </Badge>
        ) : quality === "acceptable" ? (
          <Badge className={styles.badge} color="yellow" size="s">
            Acceptable
          </Badge>
        ) : quality === "low" ? (
          <Badge className={styles.badge} color="red" size="s">
            Low quality
          </Badge>
        ) : null}
      </div>
      <div className={styles.footer}>
        <span className={styles.meta}>
          {formatLanguage(
            ...(voice.languageStatuses.length
              ? voice.languageStatuses.map(status => status.language)
              : [voice.languageId]),
          )}
        </span>
        <span className={styles.pipe} />
        <span className={styles.meta}>{formatProvider(voice.provider)}</span>
        {voice.age !== undefined ||
        voice.character !== undefined ||
        voice.gender !== undefined ? (
          <>
            <span className={styles.pipe} />
            <span className={styles.tags}>
              {voice.character === undefined ? null : (
                <Link
                  onClick={e => e.stopPropagation()}
                  to={(() => {
                    const params = new URLSearchParams(searchParams);
                    params.set("character", voice.character);
                    return `?${params.toString()}`;
                  })()}
                >
                  <Tag isGhost={true} size="s">
                    {formatCharacter(voice.character)}
                  </Tag>
                </Link>
              )}
              {voice.gender === undefined ? null : (
                <Link
                  onClick={e => e.stopPropagation()}
                  to={(() => {
                    const params = new URLSearchParams(searchParams);
                    params.set("gender", voice.gender);
                    return `?${params.toString()}`;
                  })()}
                >
                  <Tag isGhost={true} size="s">
                    {titleCaseFormatter(voice.gender)}
                  </Tag>
                </Link>
              )}
              {voice.age === undefined ? null : (
                <Link
                  onClick={e => e.stopPropagation()}
                  to={(() => {
                    const params = new URLSearchParams(searchParams);
                    params.set("age", voice.age);
                    return `?${params.toString()}`;
                  })()}
                >
                  <Tag isGhost={true} size="s">
                    {titleCaseFormatter(voice.age)}
                  </Tag>
                </Link>
              )}
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default VoiceCard;
